/**
 * rerunPrefsByType — per-report-type preference schema keyed by
 * `ReportTypeId`. Rerun Reimagined RR4.
 *
 * The Rerun panel's Configuration step renders a two-column layout:
 *   - Left: type-agnostic Project Intent fields
 *   - Right: per-type preferences — feasibility users see power /
 *     timeline / cooling; environmental sees NEPA scope / species
 *     window / wetlands prefs; etc.
 *
 * Per-type schemas already exist on `TypedPreferencesByType` in
 * `src/types/siteRag.ts` (one interface per report type). This module
 * exposes them under a consolidated alias so the Rerun-specific
 * surfaces have one ergonomic import. We deliberately do NOT duplicate
 * the schemas here — they remain a single source of truth.
 *
 * Future extension: when the rerun panel needs prefs that wouldn't
 * make sense at preflight (e.g. a "skip section X" flag the user only
 * sets on rerun), those become net-new fields on this consolidated
 * schema, not on the preflight one.
 */
import type {
  TypedPreferencesByType,
  FeasibilityPreferences,
  EnvironmentalPreferences,
  TestFitPreferences,
  PowerAvailabilityPreferences,
  WaterResourcePreferences,
  InterconnectionPreferences,
  PublicSentimentPreferences,
} from './siteRag';
import type { ReportTypeId } from '../config/reportTypes';

/** Per-type rerun preferences. Identical shape to the preflight schema for now. */
export type RerunPrefsByType = TypedPreferencesByType;

/** Per-type preference shape, addressed positionally for ergonomic use in generics. */
export type RerunPrefsFor<T extends ReportTypeId> = NonNullable<TypedPreferencesByType[T]>;

/**
 * Display labels for each report-type's preference schema. Used by the
 * RR4 Configuration step header so the right-column panel announces
 * what schema the user is editing.
 */
export const PREFS_SCHEMA_LABEL: Record<ReportTypeId, string> = {
  feasibility: 'Feasibility preferences',
  environmental: 'Environmental preferences',
  'test-fit': 'Test-fit preferences',
  'power-availability': 'Power availability preferences',
  'water-resource': 'Water resource preferences',
  interconnection: 'Interconnection preferences',
  'supply-chain-risk': 'Supply chain risk preferences',
  // PSR-1 (row 0.99290) — Public Sentiment Report.
  'public-sentiment': 'Public sentiment preferences',
  'custom-brief': 'Custom brief',
  financial: 'Financial viability preferences',
  'executive-summary': 'Executive summary',
};

/**
 * Short caption per report type — surfaces the kinds of fields the
 * user can expect to edit. The actual form rendering still flows
 * through PreferencesFormFor; this is purely the header copy.
 *
 * SRW-3 (2026-05-19) — captions were rewritten so they describe ONLY
 * the fields that survive on each per-report Preferences interface
 * post-SSOT (row 0.99187). The pre-SRW feasibility caption — "Power
 * MW, timeline, cooling preference, project lifecycle stage." —
 * referred verbatim to the four fields SSOT-1 / SSOT-3 deleted from
 * FeasibilityPreferences; the section now only carries
 * `kpiWeightOverrides`, so the old caption was dead copy. Same
 * cleanup applied to the power-availability + interconnection
 * captions where the field set was likewise misaligned.
 *
 * Every caption now ends with an "(other inputs come from Site
 * Project Intent — edit at site level)" beat where appropriate, so
 * users reading the rerun wizard understand at a glance that the
 * shared-field bucket lives elsewhere. This composes with the SRW-1
 * read-only summary card directly above.
 */
export const PREFS_SCHEMA_CAPTION: Record<ReportTypeId, string> = {
  feasibility:
    'Score-weight overrides for THIS report only. All other inputs (workload, MW, cooling, in-service date, nuclear interest, priority) come from Site Project Intent — edit at site level.',
  environmental:
    'NEPA pathway, federal nexus, species concerns, tribal proximity, air-basin override, discharge water target. (Other inputs come from Site Project Intent — edit at site level.)',
  'test-fit':
    'Target footprint, phasing, setbacks, parking ratio, stormwater detention. (Other inputs come from Site Project Intent — edit at site level.)',
  'power-availability':
    'Time-to-power critical date, renewables preference, BTM strategy, substation distance thresholds, curtailment-risk tolerance. (Power MW + other inputs come from Site Project Intent — edit at site level.)',
  'water-resource':
    'Water rights known, drought scenario, wastewater target. (Cooling type + other inputs come from Site Project Intent — edit at site level.)',
  interconnection:
    'Latency targets per hyperscaler, carrier diversity minimum, bandwidth, IX-peering requirement, submarine-cable proximity. (Other inputs come from Site Project Intent — edit at site level.)',
  'supply-chain-risk':
    'Use site-level assumptions plus BoM/supplier/quote evidence to assess procurement bottlenecks and COD risk. (Other inputs come from Site Project Intent — edit at site level.)',
  // PSR-1 (row 0.99290) — Public Sentiment Report.
  'public-sentiment':
    'Trend window, comparable-site cohort size, tribal-government records opt-in. (Other inputs come from Site Project Intent — edit at site level.)',
  'custom-brief':
    'Brief name, objective, ordered diligence questions, and optional context notes / counterparties / key dates.',
  financial:
    'Verdict hurdle rate, pinned pathway comparison, and tax / PPA adjustment toggles. The financial model is driven by Site Financial Assumptions — edit at site level.',
  'executive-summary':
    'Select which completed site reports to synthesize and optional synthesis focus notes for the BTM data-center brief.',
};

// Re-export the per-type interfaces under aliases that make the type
// dispatch ergonomic when consuming RerunPrefsFor<…>.
export type FeasibilityRerunPrefs = FeasibilityPreferences;
export type EnvironmentalRerunPrefs = EnvironmentalPreferences;
export type TestFitRerunPrefs = TestFitPreferences;
export type PowerAvailabilityRerunPrefs = PowerAvailabilityPreferences;
export type WaterResourceRerunPrefs = WaterResourcePreferences;
export type InterconnectionRerunPrefs = InterconnectionPreferences;
// PSR-1 (row 0.99290) — Public Sentiment Report rerun prefs alias.
export type PublicSentimentRerunPrefs = PublicSentimentPreferences;

/**
 * Three-band visual hierarchy for the Configuration step (CU1).
 *
 * Each report type's preference fields are split into three bands so the
 * user can see at a glance which fields move the deterministic score,
 * which are recommended for narrative quality, and which are optional
 * frame-setters that can stay collapsed on the happy path.
 *
 *   - `scoreAffecting` — mutating these fields changes the deterministic
 *     score (today only `kpiWeightOverrides` for feasibility). Rendered
 *     with a warning-toned border + an "Affects score" chip (CU4).
 *   - `recommended`    — strong narrative impact. Rendered with the
 *     default border, no collapse.
 *   - `optional`       — narrative framing or rarely-touched flags.
 *     Rendered inside a `<details>` collapsed by default.
 *
 * Field keys reference the per-type interface keys in `siteRag.ts`.
 * Fields not in any band still render through `PreferencesFormFor` but
 * fall through to the "recommended" default if no band claims them.
 */
export interface PrefsBandMembership {
  scoreAffecting: string[];
  recommended: string[];
  optional: string[];
}

/**
 * Synthetic "report type" key for the cross-cutting site-level project
 * intent fields (workloadProfile / nuclearInterest / priority). Used so
 * the rerun Configuration step can look up site-intent band membership
 * via the same `PREFS_BAND_MAP` + `bandForField()` API the per-report
 * typed-prefs use (RR5-2).
 *
 * `'siteIntent'` is not a real `ReportTypeId` — it doesn't appear in
 * `REPORT_TYPE_REGISTRY` or anywhere in the generator pipeline. It's
 * purely a UI dispatch key.
 */
export type PrefsBandKey = ReportTypeId | 'siteIntent';

export const PREFS_BAND_MAP: Record<PrefsBandKey, PrefsBandMembership> = {
  /**
   * RR5-2 — cross-cutting site project intent fields.
   *  - `workloadProfile` — score-affecting: drives deterministic scoring
   *    via type-config rules (AI-training penalises liquid-cooling
   *    absence; HPC penalises >50ms latency; etc.). Mutates Feasibility
   *    KPI weighting.
   *  - `nuclearInterest` — recommended: strong narrative impact (flips
   *    the BTM overview between SMR-first and gas-first framings) but
   *    doesn't move the deterministic score.
   *  - `priority` (cost / speed / sustainability / resilience) —
   *    recommended: reshapes which risks lead and which mitigations
   *    surface in the action plan.
   */
  // SSOT — the eight banned shared-layer fields (projectSizeMW,
  // customerCategory, inServiceDate, riskTolerance, powerSourcePreference,
  // coolingPreference, PowerAvailability.powerRequirementMW,
  // WaterResource.coolingType) live canonically on `siteIntent` — the
  // bands below list only fields that still exist on the per-report-type
  // Preferences interfaces. The `siteIntent` band absorbed the migrated
  // fields where appropriate.
  siteIntent: {
    scoreAffecting: ['workloadProfile', 'powerRequirementMW'],
    recommended: ['nuclearInterest', 'priority', 'coolingStrategy', 'inServiceDate'],
    optional: [],
  },
  feasibility: {
    scoreAffecting: ['kpiWeightOverrides'],
    recommended: [],
    optional: [],
  },
  environmental: {
    scoreAffecting: [],
    recommended: ['expectedNepaPathway', 'federalNexus'],
    optional: ['speciesConcerns', 'tribalLandProximityKnown', 'airBasinOverride', 'dischargeWaterTarget'],
  },
  'test-fit': {
    scoreAffecting: [],
    recommended: ['targetFootprintAcres', 'phasing', 'phaseCount'],
    optional: ['setbackOverrideFt', 'parkingRatioPerEmployee', 'stormwaterDetentionGal'],
  },
  'power-availability': {
    scoreAffecting: [],
    recommended: ['timeToPowerCriticalDate', 'btmStrategy'],
    optional: ['renewablesPreference', 'maxSubstationDistanceMiAmber', 'maxSubstationDistanceMiRed', 'curtailmentRiskTolerance'],
  },
  'water-resource': {
    scoreAffecting: [],
    recommended: ['waterRightsKnown'],
    optional: ['droughtScenario', 'wastewaterTarget'],
  },
  interconnection: {
    scoreAffecting: [],
    recommended: ['bandwidthGbps', 'carrierDiversityMin'],
    optional: ['latencyTargetsMs', 'ixPeeringRequired', 'submarineCableProximityImportant'],
  },
  'supply-chain-risk': {
    scoreAffecting: ['kpiWeightOverrides'],
    recommended: [],
    optional: [],
  },
  // PSR-1 (row 0.99290) — Public Sentiment Report scaffold band map.
  // No score-affecting fields (usesScoring: false). PSR-2 may add
  // theme-weighting / driver-attribution knobs.
  'public-sentiment': {
    scoreAffecting: [],
    recommended: ['trendWindowDays', 'benchmarkCohortSize'],
    optional: ['includeTribalRecords'],
  },
  'custom-brief': {
    scoreAffecting: [],
    recommended: ['briefName', 'questions', 'objective'],
    optional: ['contextNotes', 'counterparties', 'keyDates'],
  },
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. The hurdle override
  // moves the deterministic verdict banding (scoreSiteFinancial), so it is
  // score-affecting; the rest frame the presentation.
  financial: {
    scoreAffecting: ['hurdleRateOverride'],
    recommended: ['pinnedPathwayComparisonId'],
    optional: ['includeTaxCredits', 'includePpaAdjustment'],
  },
  'executive-summary': {
    scoreAffecting: [],
    recommended: ['sourceReportIds'],
    optional: ['synthesisFocus'],
  },
};

/** Lookup helper — returns the band a field belongs to, or 'recommended'
 *  if no band claims it (default visual treatment).
 *
 *  Accepts either a `ReportTypeId` (for typed-pref fields) or the
 *  synthetic `'siteIntent'` key (for cross-cutting site project intent
 *  fields — RR5-2). */
export function bandForField(
  reportType: PrefsBandKey,
  fieldKey: string,
): 'scoreAffecting' | 'recommended' | 'optional' {
  const m = PREFS_BAND_MAP[reportType];
  if (!m) return 'recommended';
  if (m.scoreAffecting.includes(fieldKey)) return 'scoreAffecting';
  if (m.optional.includes(fieldKey)) return 'optional';
  return 'recommended';
}
